import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Countries = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState('');
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    fetchCountries();
    fetchRegions();
  }, [regionFilter]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ active: 'false' });
      if (regionFilter) params.append('region', regionFilter);
      const response = await api.get(`/countries/?${params}`);
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setError('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await api.get('/countries/regions');
      setRegions(response.data.regions || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const handleSeedCountries = async () => {
    try {
      const response = await api.post('/countries/seed');
      alert(response.data.message);
      fetchCountries();
      fetchRegions();
    } catch (error) {
      console.error('Error seeding countries:', error);
      alert('Failed to seed countries');
    }
  };

  const handleDelete = async (country) => {
    if (!window.confirm(`Are you sure you want to delete ${country.name}?`)) {
      return;
    }
    
    try {
      await api.delete(`/countries/${country.id}`);
      fetchCountries();
    } catch (error) {
      console.error('Error deleting country:', error);
      alert(error.response?.data?.error || 'Failed to delete country');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Countries
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Countries
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage countries for localization and shipping
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSeedCountries}
              className="btn btn-secondary"
            >
              📥 Seed Defaults
            </button>
            <button
              onClick={() => navigate('/countries/new')}
              className="btn btn-primary"
            >
              ➕ Add Country
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Filter Bar */}
        <div style={{ marginBottom: '1rem' }}>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="form-select"
            style={{ maxWidth: '200px' }}
          >
            <option value="">All Regions</option>
            {regions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>

        {countries.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            color: 'var(--gray-500)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌍</div>
            <h3 style={{ color: 'var(--gray-700)', marginBottom: '0.5rem' }}>No countries configured</h3>
            <p style={{ marginBottom: '1rem' }}>Add countries manually or seed default countries to get started.</p>
            <button onClick={handleSeedCountries} className="btn btn-primary">
              📥 Seed Default Countries
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Code</th>
                  <th>Region</th>
                  <th>Currency</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((country) => (
                  <tr key={country.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '1.25rem', marginRight: '0.5rem' }}>
                          {country.flag_emoji || '🏳️'}
                        </span>
                        <span>{country.name}</span>
                        {country.native_name && country.native_name !== country.name && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginLeft: '0.5rem' }}>
                            ({country.native_name})
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{country.code}</strong>
                      {country.code3 && (
                        <span style={{ color: 'var(--gray-500)', marginLeft: '4px' }}>/ {country.code3}</span>
                      )}
                    </td>
                    <td>
                      {country.region}
                      {country.subregion && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          {country.subregion}
                        </div>
                      )}
                    </td>
                    <td>{country.currency_code || '-'}</td>
                    <td>{country.phone_code || '-'}</td>
                    <td>
                      {country.is_active ? (
                        <span className="badge badge-success">✓ Active</span>
                      ) : (
                        <span className="badge badge-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          onClick={() => navigate(`/countries/${country.id}/edit`)}
                          className="btn btn-sm btn-secondary"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(country)}
                          className="btn btn-sm btn-danger"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Countries;
